/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useMemo } from "react";
import { ToolbarButton } from "../../../../ui/components/Toolbar/ToolbarButton";
import { ToolbarTitle } from "../../../../ui/components/Toolbar/ToolbarTitle";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "webpack-merge/dist/utils";
import { DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";
import { useLayoutContext } from "../../../../ui/components/Layout/LayoutContext";
import { useDeepCompareCallback } from "use-deep-compare";

export interface ITestModuleConsoleToolbarProps {
	severityFilter?: Set<DOC_ERROR_SEVERITY> | null;
	onFilterSelect?: (filter: DOC_ERROR_SEVERITY) => void;
}

export const TestModuleConsoleToolbar: FC<ITestModuleConsoleToolbarProps> = ({
	severityFilter,
	onFilterSelect
}) => {
	const { panel } = useLayoutContext();
	const consoleOpen = useMemo(() => !panel || panel?.isExpanded, [ panel ]);

	const getToggleFilter = useCallback(
		(item) => () => {
			if (isFunction(onFilterSelect)) {
				const deselect = severityFilter === item && consoleOpen;
				onFilterSelect(deselect ? null : item);
			}

			if (panel && !panel.isExpanded) {
				panel.expand();
			}
		},
		[ severityFilter, onFilterSelect, panel, consoleOpen ]
	);

	const isFilterActive = useDeepCompareCallback<(severity: DOC_ERROR_SEVERITY) => boolean>(
		(severity) => severityFilter?.has(severity),
		[ severityFilter ]
	);

	const buttonClass = BEM("edt-test-module-console-toolbar", "button");

	return (
		<>
			<ToolbarTitle>Console</ToolbarTitle>
			<ToolbarButton
				icon="mdi/alert-circle"
				className={buttonClass.mod({ severity: "error" }).toClassName()}
				label="Errors"
				active={isFilterActive(DOC_ERROR_SEVERITY.ERROR)}
				onClick={getToggleFilter(DOC_ERROR_SEVERITY.ERROR)}
			/>
			<ToolbarButton
				icon="mdi/alert"
				className={buttonClass.mod({ severity: "warning" }).toClassName()}
				label="Warnings"
				active={isFilterActive(DOC_ERROR_SEVERITY.WARNING)}
				onClick={getToggleFilter(DOC_ERROR_SEVERITY.WARNING)}
			/>
			<ToolbarButton
				icon="mdi/lightbulb"
				className={buttonClass.mod({ severity: "hint" }).toClassName()}
				label="Hints"
				active={isFilterActive(DOC_ERROR_SEVERITY.HINT)}
				onClick={getToggleFilter(DOC_ERROR_SEVERITY.HINT)}
			/>
		</>
	);
};
